<template>
  <Dialog
    :visible="modelValue"
    :header="isEdit ? 'Редактировать слайд' : 'Новый слайд'"
    :modal="true"
    :style="{ width: '850px' }"
    @update:visible="handleClose"
  >
    <div class="form-container">
      <div class="form-section">
        <div class="section-title">
          <i class="pi pi-image"></i>
          <span>Изображение слайда</span>
        </div>

        <div class="field">
          <div v-if="form.imageUrl" class="image-preview">
            <img :src="form.imageUrl" class="preview-img" />
            <div class="preview-overlay">
              <Button
                icon="pi pi-trash"
                severity="danger"
                rounded
                text
                title="Удалить"
                @click.stop="removeImage"
              />
            </div>
          </div>

          <div v-else class="image-upload-area">
            <div class="upload-actions">
              <Button
                label="Загрузить файл"
                icon="pi pi-upload"
                severity="secondary"
                outlined
                @click="triggerFileInput"
              />
              <Button
                label="По URL"
                icon="pi pi-link"
                severity="secondary"
                outlined
                @click="openUrlInput"
              />
              <Button
                label="Из галереи"
                icon="pi pi-images"
                severity="secondary"
                outlined
                @click="openGallery"
              />
            </div>

            <input
              ref="fileInput"
              type="file"
              accept="image/*"
              class="hidden"
              @change="handleFileUpload"
            />

            <div v-if="uploading" class="uploading-status">
              <i class="pi pi-spin pi-spinner"></i>
              Загрузка изображения...
            </div>

            <div v-if="showUrlInput" class="url-input">
              <InputText
                v-model="urlInputValue"
                placeholder="Введите URL изображения"
                class="w-full"
              />
              <div class="url-actions">
                <Button
                  label="Отмена"
                  severity="secondary"
                  text
                  @click="cancelUrlInput"
                />
                <Button label="Применить" @click="applyUrl" />
              </div>
            </div>
          </div>
          <small v-if="errors.imageUrl" class="field-error">
            {{ errors.imageUrl }}
          </small>
        </div>
      </div>

      <div class="form-section">
        <div class="section-title">
          <i class="pi pi-info-circle"></i>
          <span>Основная информация</span>
        </div>

        <div class="two-columns">
          <div class="field">
            <label>Заголовок</label>
            <InputText
              v-model="form.title"
              placeholder="Введите заголовок"
              :invalid="!!errors.title"
            />
            <small v-if="errors.title" class="field-error">{{
              errors.title
            }}</small>
          </div>

          <div class="field">
            <label>Текст кнопки</label>
            <InputText
              v-model="form.buttonText"
              placeholder="Узнать больше →"
            />
            <small class="field-hint"
              >Оставьте пустым, чтобы скрыть кнопку</small
            >
          </div>
        </div>

        <div class="field">
          <label>Подзаголовок</label>
          <Textarea
            v-model="form.subtitle"
            rows="2"
            placeholder="Введите подзаголовок"
          />
        </div>

        <div class="field">
          <label>Ссылка при клике</label>
          <InputText
            v-model="form.link"
            placeholder="https://... или /products/123"
          />
          <small class="field-hint"
            >Куда перейдёт пользователь при клике на кнопку или слайд</small
          >
        </div>
      </div>

      <div class="form-section">
        <div class="section-title">
          <i class="pi pi-align-center"></i>
          <span>Настройки текста</span>
        </div>

        <div class="two-columns">
          <div class="field">
            <label>Позиция текста</label>
            <SelectButton
              v-model="form.titlePosition"
              :options="positionOptions"
              optionLabel="label"
              optionValue="value"
            />
            <small class="field-hint"
              >Где будет расположен текст на слайде</small
            >
          </div>

          <div class="field">
            <label>Выравнивание текста</label>
            <SelectButton
              v-model="form.textAlign"
              :options="alignOptions"
              optionLabel="label"
              optionValue="value"
            />
          </div>
        </div>

        <div class="two-columns">
          <div class="field">
            <label>Размер заголовка</label>
            <div class="size-control">
              <InputNumber v-model="form.titleSize" :min="10" :max="120" />
              <span class="size-unit">px</span>
            </div>
          </div>

          <div class="field">
            <label>Размер подзаголовка</label>
            <div class="size-control">
              <InputNumber v-model="form.subtitleSize" :min="10" :max="120" />
              <span class="size-unit">px</span>
            </div>
          </div>
        </div>

        <div class="two-columns">
          <div class="field">
            <label>Цвет заголовка</label>
            <div class="color-control">
              <input
                type="color"
                v-model="form.titleColor"
                class="color-picker"
              />
              <InputText v-model="form.titleColor" placeholder="#FFFFFF" />
            </div>
          </div>

          <div class="field">
            <label>Цвет подзаголовка</label>
            <div class="color-control">
              <input
                type="color"
                v-model="form.subtitleColor"
                class="color-picker"
              />
              <InputText v-model="form.subtitleColor" placeholder="#E0E0E0" />
            </div>
          </div>
        </div>
      </div>

      <div class="form-section">
        <div class="section-title">
          <i class="pi pi-flag"></i>
          <span>Статус</span>
        </div>

        <div class="field">
          <SelectButton
            v-model="form.isActive"
            :options="statusOptions"
            optionLabel="label"
            optionValue="value"
          />
          <small class="field-hint"
            >Неактивные слайды не отображаются на сайте</small
          >
        </div>
      </div>
    </div>

    <Dialog
      v-model:visible="galleryVisible"
      header="Галерея изображений"
      :style="{ width: '800px' }"
      modal
    >
      <div class="gallery-grid">
        <div
          v-for="img in galleryImages"
          :key="img.url"
          class="gallery-item"
          @click="selectFromGallery(img.url)"
        >
          <img :src="img.url" :alt="img.name" />
          <div class="gallery-item-name">{{ img.name }}</div>
        </div>
        <div v-if="galleryImages.length === 0" class="gallery-empty">
          <i class="pi pi-image text-4xl text-gray-300 mb-2" />
          <p>Нет загруженных изображений</p>
        </div>
      </div>
      <template #footer>
        <Button
          label="Закрыть"
          severity="secondary"
          @click="galleryVisible = false"
        />
      </template>
    </Dialog>

    <template #footer>
      <Button
        label="Отмена"
        severity="secondary"
        outlined
        @click="handleClose"
      />
      <Button label="Сохранить" :loading="submitting" @click="handleSave" />
    </template>
  </Dialog>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import InputNumber from "primevue/inputnumber";
import SelectButton from "primevue/selectbutton";
import { uploadImage, getGalleryImages } from "@/shared/api/files.service";
import { useToast } from "primevue/usetoast";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  item: { type: Object, default: null },
  submitting: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue", "save"]);

const toast = useToast();
const uploading = ref(false);
const fileInput = ref(null);
const showUrlInput = ref(false);
const urlInputValue = ref("");
const galleryVisible = ref(false);
const galleryImages = ref([]);
const errors = ref({});

const isEdit = computed(() => !!props.item?.id);

const positionOptions = [
  { label: "Сверху", value: "top" },
  { label: "По центру", value: "center" },
  { label: "Снизу", value: "bottom" },
];

const alignOptions = [
  { label: "По левому краю", value: "left" },
  { label: "По центру", value: "center" },
  { label: "По правому краю", value: "right" },
];

const statusOptions = [
  { label: "Активен", value: true },
  { label: "Неактивен", value: false },
];

const form = ref({
  id: null,
  title: "",
  subtitle: "",
  imageUrl: "",
  link: "",
  buttonText: "Узнать больше →",
  titlePosition: "center",
  textAlign: "center",
  titleSize: 42,
  subtitleSize: 16,
  titleColor: "#FFFFFF",
  subtitleColor: "#E0E0E0",
  isActive: true,
});

watch(
  () => props.modelValue,
  (val) => {
    if (val && props.item) {
      form.value = { ...props.item, title: props.item.title ?? "" };
    } else if (val && !props.item) {
      resetForm();
    }
  },
);

function resetForm() {
  form.value = {
    id: null,
    title: "",
    subtitle: "",
    imageUrl: "",
    link: "",
    buttonText: "Узнать больше →",
    titlePosition: "center",
    textAlign: "center",
    titleSize: 42,
    subtitleSize: 16,
    titleColor: "#FFFFFF",
    subtitleColor: "#E0E0E0",
    isActive: true,
  };
  errors.value = {};
  showUrlInput.value = false;
  urlInputValue.value = "";
}

function triggerFileInput() {
  fileInput.value?.click();
}

async function handleFileUpload(event) {
  const file = event.target.files?.[0];
  if (!file) return;

  uploading.value = true;
  try {
    const result = await uploadImage(file, "homepage");
    form.value.imageUrl = result.url;
    errors.value.imageUrl = "";
    toast.add({
      severity: "success",
      summary: "Успешно",
      detail: "Изображение загружено",
      life: 3000,
    });
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Ошибка",
      detail: error.message || "Ошибка загрузки",
      life: 5000,
    });
  } finally {
    uploading.value = false;
    fileInput.value.value = "";
  }
}

function openUrlInput() {
  showUrlInput.value = true;
}

function cancelUrlInput() {
  showUrlInput.value = false;
  urlInputValue.value = "";
}

function applyUrl() {
  if (urlInputValue.value.trim()) {
    form.value.imageUrl = urlInputValue.value.trim();
    errors.value.imageUrl = "";
    showUrlInput.value = false;
    urlInputValue.value = "";
    toast.add({
      severity: "success",
      summary: "Успешно",
      detail: "URL добавлен",
      life: 3000,
    });
  }
}

async function openGallery() {
  await loadGalleryImages();
  galleryVisible.value = true;
}

async function loadGalleryImages() {
  try {
    galleryImages.value = await getGalleryImages("homepage");
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Ошибка",
      detail: "Ошибка загрузки галереи",
      life: 5000,
    });
  }
}

function selectFromGallery(url) {
  form.value.imageUrl = url;
  errors.value.imageUrl = "";
  galleryVisible.value = false;
  toast.add({
    severity: "success",
    summary: "Успешно",
    detail: "Изображение выбрано",
    life: 3000,
  });
}

function removeImage() {
  form.value.imageUrl = "";
}

function validate() {
  errors.value = {};
  if ((form.value.title?.length ?? 0) > 255)
    errors.value.title = "Заголовок не может превышать 255 символов";
  if (!form.value.imageUrl?.trim())
    errors.value.imageUrl = "Изображение обязательно";
  return Object.keys(errors.value).length === 0;
}

function handleSave() {
  if (!validate()) return;
  emit("save", {
    ...form.value,
    title: form.value.title?.trim() ? form.value.title : null,
  });
}

function handleClose() {
  if (props.submitting) return;
  emit("update:modelValue", false);
  resetForm();
}
</script>

<style scoped>
.form-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-section {
  background: #fafafa;
  border-radius: 16px;
  padding: 20px;
  border: 1px solid #eee;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
}

.section-title i {
  color: #c41217;
  font-size: 18px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.field:last-child {
  margin-bottom: 0;
}

.field label {
  font-weight: 500;
  font-size: 13px;
  color: #555;
}

.two-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.image-preview {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: #f5f5f5;
  aspect-ratio: 16 / 9;
}

.preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  opacity: 0;
  transition: opacity 0.2s;
}

.image-preview:hover .preview-overlay {
  opacity: 1;
}

.image-upload-area {
  border: 2px dashed #ddd;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  background: #fafafa;
  transition: all 0.2s;
}

.image-upload-area:hover {
  border-color: #c41217;
  background: #fff;
}

.upload-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.uploading-status {
  margin-top: 16px;
  padding: 12px;
  background: #e3f2fd;
  border-radius: 8px;
  color: #1976d2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.url-input {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.url-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.size-control {
  display: flex;
  align-items: center;
  gap: 8px;
}

.size-control :deep(.p-inputnumber) {
  flex: 1;
}

.size-unit {
  color: #666;
  font-size: 14px;
}

.color-control {
  display: flex;
  align-items: center;
  gap: 12px;
}

.color-picker {
  width: 40px;
  height: 40px;
  border: 1px solid #ddd;
  border-radius: 8px;
  cursor: pointer;
}

.color-control :deep(.p-inputtext) {
  flex: 1;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
  max-height: 500px;
  overflow-y: auto;
  padding: 8px;
}

.gallery-item {
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s;
  text-align: center;
  background: #f5f5f5;
}

.gallery-item:hover {
  border-color: #c41217;
  transform: scale(1.02);
}

.gallery-item img {
  width: 100%;
  height: 120px;
  object-fit: cover;
}

.gallery-item-name {
  padding: 8px;
  font-size: 12px;
  color: #666;
  word-break: break-all;
}

.gallery-empty {
  text-align: center;
  padding: 60px;
  color: #999;
}

.field-error {
  color: #dc2626;
  font-size: 12px;
}

.field-hint {
  color: #888;
  font-size: 11px;
}

@media (max-width: 700px) {
  .two-columns {
    grid-template-columns: 1fr;
  }

  .upload-actions {
    flex-direction: column;
  }

  .gallery-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }

  .form-section {
    padding: 16px;
  }
}
</style>
